import classNames from 'classnames';
import React, {
  ElementType,
  FC,
  MouseEventHandler,
  MutableRefObject,
  PropsWithChildren,
} from 'react';

import { ButtonTypes } from '../Button/Button.types';
import { IconView } from '../IconView/IconView';
import { IconViewSizes } from '../IconView/IconView.types';
import { Loader, LoaderColor } from '../Loader/Loader';
import styles from './IconButton.styles.m.css';
import { IconButtonShape, TIconButtonTheme } from './types';

export { IconViewSizes, IconButtonShape };

interface IIconButtonProps {
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onContextMenu?(): void;
  onMouseEnter?(): void;
  onMouseLeave?(): void;
  isDisabled?: boolean;
  isLoading?: boolean;
  Icon: ElementType;
  hasBadge?: boolean;
  className?: string;
  iconClassName?: string;
  isActive?: boolean;
  shape?: IconButtonShape;
  size?: IconViewSizes;
  type?: ButtonTypes;
  iconSize?: IconViewSizes;
  theme?: TIconButtonTheme;
  loaderColor?: LoaderColor;
  setRef?: MutableRefObject<HTMLButtonElement | null>;
  dataTest?: string;
  title?: string;
  text?: React.ReactNode;
  textContainerClassName?: string;
}

export const IconButton: FC<PropsWithChildren<IIconButtonProps>> = (props) => {
  const {
    onClick,
    onContextMenu,
    onMouseEnter,
    onMouseLeave,
    isDisabled,
    isLoading,
    Icon,
    hasBadge,
    iconClassName,
    isActive,
    className,
    shape = IconButtonShape.Round,
    size = IconViewSizes.Medium,
    type = ButtonTypes.Button,
    iconSize,
    theme = '2',
    loaderColor = 'secondary',
    setRef,
    dataTest,
    title,
    text,
    textContainerClassName,
  } = props;
  const containerClasses = classNames(
    styles.container,
    styles[size],
    styles[`shape-${shape}`],
    {
      [styles.disabled]: isDisabled,
      [styles.active]: isActive,
    },
    styles[`theme-${theme}`],
    className
  );

  const handleMouseDown: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
  };

  const iconClasses = classNames(styles.icon, iconClassName, {
    [styles.disabledIcon]: isDisabled || isLoading,
    [styles.hiddenIcon]: isLoading,
  });

  return (
    <button
      className={containerClasses}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={isDisabled || isLoading}
      ref={setRef}
      type={type}
      data-test={dataTest}
      title={title}
      onMouseDown={handleMouseDown}
    >
      {hasBadge && <div className={styles.dotBadge} />}
      <IconView
        Icon={Icon}
        size={iconSize || size}
        className={iconClasses}
      />
      {props.children}
      {text && <span className={classNames(styles.text, textContainerClassName)}>{text}</span>}
      {isLoading && (
        <Loader
          color={loaderColor}
          className={styles.loader}
        />
      )}
    </button>
  );
};
